import type { ListingPropertyType } from "@/lib/listing-property-type";
import type { ListingStatus } from "@/lib/listing-status";

export interface MockProperty {
  id: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  lat: number;
  lng: number;
  price: number;
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  yearBuilt: number;
  propertyType: "sfh" | "mfh" | "condo" | "townhouse" | "land" | "commercial";
  /**
   * Top-level MLS classification behind `propertyType` — Residential vs
   * Commercial vs Land. Optional because rows cached before the field existed
   * won't carry it; the card pill is simply omitted in that case.
   */
  listingPropertyType?: ListingPropertyType | null;
  /**
   * True when `price` is a monthly rent rather than a sale price. `property-search`
   * filters these out, so it should only ever be false on a live result — it is
   * carried through so a stale saved row can still be recognized.
   */
  isLease?: boolean;
  /**
   * Market listing status from the feed. Widened to the full vocabulary when
   * `mapStatus` in the property-search function stopped collapsing RESO
   * CLOSED/SOLD/EXPIRED into "active".
   */
  status: ListingStatus;
  daysOnMarket: number;
  priceReduction?: number;
  imageUrl: string;
  
  // Investor metrics
  estimatedRent: number;
  arvEstimate: number;
  repairEstimate: number;
  capRate: number;
  cashOnCash: number;
  grossRentMultiplier: number;
  section8Fmr: number;
  vacancyRate: number;
  
  // Distress indicators
  isForeclosure: boolean;
  isPreForeclosure: boolean;
  isAuction: boolean;
  isReo: boolean;
  isBankOwned: boolean;
  
  // Deal indicators
  equityEstimate: number;
  motivatedSeller: boolean;
  belowMarketValue: boolean;
}

// Generate realistic mock properties across major US markets
// Section 8 FMR values are based on HUD FY 2026 Fair Market Rents
export const mockProperties: MockProperty[] = [
  {
    id: "1",
    address: "1234 Oak Street",
    city: "Dallas",
    state: "TX",
    zip: "75201",
    lat: 32.7767,
    lng: -96.7970,
    price: 285000,
    bedrooms: 3,
    bathrooms: 2,
    sqft: 1850,
    yearBuilt: 1995,
    propertyType: "sfh",
    status: "active",
    daysOnMarket: 45,
    priceReduction: 15000,
    imageUrl: "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400&h=300&fit=crop",
    estimatedRent: 2100,
    arvEstimate: 340000,
    repairEstimate: 25000,
    capRate: 7.2,
    cashOnCash: 12.5,
    grossRentMultiplier: 11.3,
    section8Fmr: 2089, // FY 2026 Dallas-Fort Worth 3BR FMR
    vacancyRate: 4.2,
    isForeclosure: false,
    isPreForeclosure: false,
    isAuction: false,
    isReo: false,
    isBankOwned: false,
    equityEstimate: 55000,
    motivatedSeller: true,
    belowMarketValue: true,
  },
  {
    id: "2",
    address: "567 Maple Avenue",
    city: "Houston",
    state: "TX",
    zip: "77002",
    lat: 29.7604,
    lng: -95.3698,
    price: 175000,
    bedrooms: 4,
    bathrooms: 2,
    sqft: 2100,
    yearBuilt: 1978,
    propertyType: "sfh",
    status: "price_reduced",
    daysOnMarket: 92,
    priceReduction: 25000,
    imageUrl: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=400&h=300&fit=crop",
    estimatedRent: 1650,
    arvEstimate: 265000,
    repairEstimate: 45000,
    capRate: 8.1,
    cashOnCash: 15.3,
    grossRentMultiplier: 8.8,
    section8Fmr: 1849, // FY 2026 Houston 4BR FMR
    vacancyRate: 5.1,
    isForeclosure: true,
    isPreForeclosure: false,
    isAuction: false,
    isReo: false,
    isBankOwned: false,
    equityEstimate: 90000,
    motivatedSeller: true,
    belowMarketValue: true,
  },
  {
    id: "3",
    address: "890 Pine Road",
    city: "Atlanta",
    state: "GA",
    zip: "30301",
    lat: 33.7490,
    lng: -84.3880,
    price: 320000,
    bedrooms: 3,
    bathrooms: 2.5,
    sqft: 2200,
    yearBuilt: 2005,
    propertyType: "sfh",
    status: "active",
    daysOnMarket: 14,
    imageUrl: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&h=300&fit=crop",
    estimatedRent: 2400,
    arvEstimate: 355000,
    repairEstimate: 8000,
    capRate: 6.8,
    cashOnCash: 10.2,
    grossRentMultiplier: 11.1,
    section8Fmr: 2156, // FY 2026 Atlanta 3BR FMR
    vacancyRate: 3.8,
    isForeclosure: false,
    isPreForeclosure: false,
    isAuction: false,
    isReo: false,
    isBankOwned: false,
    equityEstimate: 35000,
    motivatedSeller: false,
    belowMarketValue: false,
  },
  {
    id: "4",
    address: "234 Elm Court",
    city: "Phoenix",
    state: "AZ",
    zip: "85001",
    lat: 33.4484,
    lng: -112.0740,
    price: 198000,
    bedrooms: 3,
    bathrooms: 1.5,
    sqft: 1400,
    yearBuilt: 1972,
    propertyType: "sfh",
    status: "active",
    daysOnMarket: 67,
    priceReduction: 12000,
    imageUrl: "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=400&h=300&fit=crop",
    estimatedRent: 1550,
    arvEstimate: 275000,
    repairEstimate: 35000,
    capRate: 7.9,
    cashOnCash: 14.1,
    grossRentMultiplier: 10.6,
    section8Fmr: 1722, // FY 2026 Phoenix 3BR FMR
    vacancyRate: 4.5,
    isForeclosure: false,
    isPreForeclosure: true,
    isAuction: false,
    isReo: false,
    isBankOwned: false,
    equityEstimate: 77000,
    motivatedSeller: true,
    belowMarketValue: true,
  },
  {
    id: "5",
    address: "789 Cedar Lane",
    city: "Indianapolis",
    state: "IN",
    zip: "46201",
    lat: 39.7684,
    lng: -86.1581,
    price: 125000,
    bedrooms: 4,
    bathrooms: 2,
    sqft: 1800,
    yearBuilt: 1960,
    propertyType: "sfh",
    status: "price_reduced",
    daysOnMarket: 120,
    priceReduction: 20000,
    imageUrl: "https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?w=400&h=300&fit=crop",
    estimatedRent: 1200,
    arvEstimate: 185000,
    repairEstimate: 28000,
    capRate: 9.2,
    cashOnCash: 18.5,
    grossRentMultiplier: 8.7,
    section8Fmr: 1494, // FY 2026 Indianapolis 4BR FMR
    vacancyRate: 6.2,
    isForeclosure: false,
    isPreForeclosure: false,
    isAuction: true,
    isReo: false,
    isBankOwned: false,
    equityEstimate: 60000,
    motivatedSeller: true,
    belowMarketValue: true,
  },
  {
    id: "6",
    address: "456 Birch Street",
    city: "Memphis",
    state: "TN",
    zip: "38103",
    lat: 35.1495,
    lng: -90.0490,
    price: 89000,
    bedrooms: 3,
    bathrooms: 1,
    sqft: 1100,
    yearBuilt: 1955,
    propertyType: "sfh",
    status: "active",
    daysOnMarket: 34,
    imageUrl: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=400&h=300&fit=crop",
    estimatedRent: 950,
    arvEstimate: 135000,
    repairEstimate: 22000,
    capRate: 10.5,
    cashOnCash: 22.1,
    grossRentMultiplier: 7.8,
    section8Fmr: 1300, // FY 2026 Memphis 3BR FMR
    vacancyRate: 7.5,
    isForeclosure: false,
    isPreForeclosure: false,
    isAuction: false,
    isReo: true,
    isBankOwned: true,
    equityEstimate: 46000,
    motivatedSeller: true,
    belowMarketValue: true,
  },
  {
    id: "7",
    address: "321 Walnut Drive",
    city: "Cleveland",
    state: "OH",
    zip: "44101",
    lat: 41.4993,
    lng: -81.6944,
    price: 78000,
    bedrooms: 3,
    bathrooms: 1.5,
    sqft: 1350,
    yearBuilt: 1948,
    propertyType: "sfh",
    status: "active",
    daysOnMarket: 28,
    imageUrl: "https://images.unsplash.com/photo-1576941089067-2de3c901e126?w=400&h=300&fit=crop",
    estimatedRent: 1050,
    arvEstimate: 125000,
    repairEstimate: 18000,
    capRate: 12.1,
    cashOnCash: 26.4,
    grossRentMultiplier: 6.2,
    section8Fmr: 1341, // FY 2026 Cleveland 3BR FMR
    vacancyRate: 8.1,
    isForeclosure: false,
    isPreForeclosure: false,
    isAuction: false,
    isReo: false,
    isBankOwned: false,
    equityEstimate: 47000,
    motivatedSeller: false,
    belowMarketValue: true,
  },
  {
    id: "8",
    address: "654 Spruce Avenue",
    city: "Kansas City",
    state: "MO",
    zip: "64101",
    lat: 39.0997,
    lng: -94.5786,
    price: 145000,
    bedrooms: 4,
    bathrooms: 2,
    sqft: 1650,
    yearBuilt: 1985,
    propertyType: "sfh",
    status: "pending",
    daysOnMarket: 8,
    imageUrl: "https://images.unsplash.com/photo-1600566753190-17f0baa2a6c3?w=400&h=300&fit=crop",
    estimatedRent: 1400,
    arvEstimate: 175000,
    repairEstimate: 12000,
    capRate: 8.6,
    cashOnCash: 16.2,
    grossRentMultiplier: 8.6,
    section8Fmr: 1583, // FY 2026 Kansas City 4BR FMR
    vacancyRate: 5.3,
    isForeclosure: false,
    isPreForeclosure: false,
    isAuction: false,
    isReo: false,
    isBankOwned: false,
    equityEstimate: 30000,
    motivatedSeller: false,
    belowMarketValue: false,
  },
  {
    id: "9",
    address: "987 Ash Boulevard",
    city: "Birmingham",
    state: "AL",
    zip: "35201",
    lat: 33.5207,
    lng: -86.8025,
    price: 95000,
    bedrooms: 3,
    bathrooms: 2,
    sqft: 1450,
    yearBuilt: 1968,
    propertyType: "sfh",
    status: "price_reduced",
    daysOnMarket: 85,
    priceReduction: 15000,
    imageUrl: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=400&h=300&fit=crop",
    estimatedRent: 1100,
    arvEstimate: 155000,
    repairEstimate: 30000,
    capRate: 10.8,
    cashOnCash: 21.5,
    grossRentMultiplier: 7.2,
    section8Fmr: 1301, // FY 2026 Birmingham 3BR FMR
    vacancyRate: 6.8,
    isForeclosure: true,
    isPreForeclosure: false,
    isAuction: false,
    isReo: false,
    isBankOwned: false,
    equityEstimate: 60000,
    motivatedSeller: true,
    belowMarketValue: true,
  },
  {
    id: "10",
    address: "159 Cherry Street",
    city: "Jacksonville",
    state: "FL",
    zip: "32099",
    lat: 30.3322,
    lng: -81.6557,
    price: 225000,
    bedrooms: 4,
    bathrooms: 2.5,
    sqft: 2000,
    yearBuilt: 2002,
    propertyType: "sfh",
    status: "active",
    daysOnMarket: 21,
    imageUrl: "https://images.unsplash.com/photo-1600573472550-8090b5e0745e?w=400&h=300&fit=crop",
    estimatedRent: 1900,
    arvEstimate: 265000,
    repairEstimate: 15000,
    capRate: 7.5,
    cashOnCash: 13.8,
    grossRentMultiplier: 9.9,
    section8Fmr: 1989, // FY 2026 Jacksonville 4BR FMR
    vacancyRate: 4.0,
    isForeclosure: false,
    isPreForeclosure: false,
    isAuction: false,
    isReo: false,
    isBankOwned: false,
    equityEstimate: 40000,
    motivatedSeller: false,
    belowMarketValue: false,
  },
  {
    id: "11",
    address: "753 Willow Way",
    city: "Charlotte",
    state: "NC",
    zip: "28201",
    lat: 35.2271,
    lng: -80.8431,
    price: 275000,
    bedrooms: 3,
    bathrooms: 2,
    sqft: 1750,
    yearBuilt: 2010,
    propertyType: "sfh",
    status: "contingent",
    daysOnMarket: 12,
    imageUrl: "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?w=400&h=300&fit=crop",
    estimatedRent: 2000,
    arvEstimate: 295000,
    repairEstimate: 5000,
    capRate: 6.5,
    cashOnCash: 9.8,
    grossRentMultiplier: 11.5,
    section8Fmr: 2045, // FY 2026 Charlotte 3BR FMR
    vacancyRate: 3.5,
    isForeclosure: false,
    isPreForeclosure: false,
    isAuction: false,
    isReo: false,
    isBankOwned: false,
    equityEstimate: 20000,
    motivatedSeller: false,
    belowMarketValue: false,
  },
  {
    id: "12",
    address: "852 Hickory Lane",
    city: "Detroit",
    state: "MI",
    zip: "48201",
    lat: 42.3314,
    lng: -83.0458,
    price: 45000,
    bedrooms: 4,
    bathrooms: 1.5,
    sqft: 1600,
    yearBuilt: 1925,
    propertyType: "sfh",
    status: "active",
    daysOnMarket: 55,
    imageUrl: "https://images.unsplash.com/photo-1600566752355-35792bedcfea?w=400&h=300&fit=crop",
    estimatedRent: 850,
    arvEstimate: 95000,
    repairEstimate: 25000,
    capRate: 15.2,
    cashOnCash: 32.1,
    grossRentMultiplier: 4.4,
    section8Fmr: 1489, // FY 2026 Detroit 4BR FMR
    vacancyRate: 12.5,
    isForeclosure: false,
    isPreForeclosure: false,
    isAuction: false,
    isReo: true,
    isBankOwned: true,
    equityEstimate: 50000,
    motivatedSeller: true,
    belowMarketValue: true,
  },
];

export const propertyTypeLabels: Record<string, string> = {
  sfh: "Single Family",
  mfh: "Multi-Family",
  condo: "Condo",
  townhouse: "Townhouse",
  land: "Land",
  commercial: "Commercial",
};

/**
 * Status labels are shared with the pipeline's listing-status pill — one
 * vocabulary, one set of words. Find keeps its own colours below (its palette
 * predates the pipeline pill and reads against photo thumbnails).
 */
export { LISTING_STATUS_LABELS as statusLabels } from "@/lib/listing-status";

export const statusColors: Record<string, string> = {
  active: "bg-success text-success-foreground",
  pending: "bg-warning text-warning-foreground",
  contingent: "bg-secondary text-secondary-foreground",
  price_reduced: "bg-destructive text-destructive-foreground",
  sold: "bg-muted-foreground text-background",
  off_market: "bg-muted text-muted-foreground",
};
